import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { evenementService, authService } from '../../api';
import './Evenement.css';
import Header from '../Header/Header';
import { FiMapPin, FiCalendar, FiDollarSign, FiUser, FiImage, FiFilter, FiX, FiSearch, FiVideo } from 'react-icons/fi';
import bannerImage from '../../assets/images/papier-peint-bleu-carre.jpg';
import Footer from '../footer/Footer';

const Evenement = () => {
  const [evenements, setEvenements] = useState([]);
  const [filteredEvenements, setFilteredEvenements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [user, setUser] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const navigate = useNavigate();
  const suggestionsRef = useRef(null);

  // États pour les filtres
  const [filters, setFilters] = useState({
    theme: '',
    lieu: '',
    ville: ''
  });

  // Options pour les filtres
  const themes = ['Formation', 'Conférence', 'Atelier', 'Networking', 'Autre'];
  const lieux = ['Présentiel', 'Webinaire'];

  // Liste des villes et régions françaises pour l'autocomplétion
  const frenchCities = [
    'Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier', 
    'Bordeaux', 'Lille', 'Rennes', 'Reims', 'Le Havre', 'Saint-Étienne', 'Toulon', 'Grenoble', 
    'Dijon', 'Angers', 'Nîmes', 'Villeurbanne', 'Aix-en-Provence', 'Clermont-Ferrand', 'Brest', 
    'Limoges', 'Tours', 'Amiens', 'Perpignan', 'Metz', 'Besançon', 'Boulogne-Billancourt', 
    'Orléans', 'Mulhouse', 'Rouen', 'Caen', 'Nancy', 'Saint-Denis', 'Argenteuil', 'Montreuil', 
    'Roubaix', 'Dunkerque', 'Avignon', 'Créteil', 'Poitiers', 'Fort-de-France', 'Courbevoie', 
    'Versailles', 'Nanterre', 'Vitry-sur-Seine', 'Antibes', 'Colombes', 'Auvergne-Rhône-Alpes',
    'Bourgogne-Franche-Comté', 'Bretagne', 'Centre-Val de Loire', 'Corse', 'Grand Est', 
    'Hauts-de-France', 'Île-de-France', 'Normandie', 'Nouvelle-Aquitaine', 'Occitanie', 
    'Pays de la Loire', 'Provence-Alpes-Côte d\'Azur', 'Guadeloupe', 'Martinique', 'Guyane', 
    'La Réunion', 'Mayotte', 'France entière'
  ];

  // Gestionnaire de clic en dehors des suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authService.getProfile();
          setUser(response);
        } catch (err) {
          console.error('Erreur de récupération du profil:', err);
          authService.logout();
        }
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const fetchEvenements = async () => {
      try {
        setLoading(true);
        const response = await evenementService.getAll(currentPage);

        const eventsData = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        console.log('Données des événements:', eventsData);

        const eventsWithInscriptionStatus = await Promise.all(
          eventsData.map(async (event) => {
            const imageUrl = getImageUrl(event.image);
            
            console.log(`Event ${event.id} - Image: ${event.image} -> URL: ${imageUrl}`);
            
            if (user) {
              try {
                const res = await evenementService.checkInscription(event.id);
                return { 
                  ...event, 
                  isInscrit: res.is_inscrit || false,
                  imageUrl: imageUrl
                };
              } catch (error) {
                return { 
                  ...event, 
                  isInscrit: false,
                  imageUrl: imageUrl
                };
              }
            }
            return {
              ...event,
              imageUrl: imageUrl
            };
          })
        );

        setEvenements(eventsWithInscriptionStatus);
        setFilteredEvenements(eventsWithInscriptionStatus);

        if (response?.last_page) {
          setLastPage(response.last_page);
        } else {
          setLastPage(1);
        }

        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des événements');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvenements();
  }, [currentPage, user]);

  // Filtrer les événements lorsque les filtres changent
  useEffect(() => {
    let results = evenements;
    
    if (filters.theme) {
      results = results.filter(event => 
        event.titre.toLowerCase().includes(filters.theme.toLowerCase()) || 
        (event.description && event.description.toLowerCase().includes(filters.theme.toLowerCase()))
      );
    }
    
    if (filters.lieu) {
      results = results.filter(event => 
        event.lieu && event.lieu.toLowerCase().includes(filters.lieu.toLowerCase())
      );
    }
    
    if (filters.ville && filters.ville !== 'France entière') {
      results = results.filter(event => 
        event.ville && event.ville.toLowerCase().includes(filters.ville.toLowerCase())
      );
    }
    
    setFilteredEvenements(results);
  }, [filters, evenements]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      console.log('Aucun chemin d\'image fourni');
      return '/default-event.jpg';
    }
    
    console.log('Chemin image original:', imagePath);
    
    // Si l'image est déjà une URL complète
    if (imagePath.startsWith('http')) {
      console.log('URL complète détectée:', imagePath);
      return imagePath;
    }
    
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    let finalUrl = '';
    
    // Nettoyer le chemin
    const cleanedPath = imagePath.startsWith('storage/') 
      ? imagePath.replace('storage/', '') 
      : imagePath;
    
    console.log('Chemin nettoyé:', cleanedPath);
    
    // Vérifier si le chemin contient déjà 'evenements/'
    if (cleanedPath.includes('evenements/')) {
      // Le chemin est déjà correct, ex: 'evenements/filename.jpg'
      finalUrl = `${baseUrl}/storage/${cleanedPath}`;
    } else if (cleanedPath.includes('/')) {
      // Le chemin contient un dossier personnalisé
      finalUrl = `${baseUrl}/storage/${cleanedPath}`;
    } else {
      // Juste un nom de fichier, utiliser le dossier par défaut
      finalUrl = `${baseUrl}/storage/evenements/${cleanedPath}`;
    }
    
    console.log('URL finale construite:', finalUrl);
    return finalUrl;
  };

  // Fonction pour gérer la saisie dans le champ ville
  const handleCityInputChange = (value) => {
    handleFilterChange('ville', value);
    
    if (value.length > 1) {
      const filteredSuggestions = frenchCities.filter(city =>
        city.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Fonction pour sélectionner une suggestion
  const handleSuggestionClick = (suggestion) => {
    handleFilterChange('ville', suggestion);
    setShowSuggestions(false);
  };

  const resetFilters = () => {
    setFilters({
      theme: '',
      lieu: '',
      ville: ''
    });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleInscription = async (evenementId, isCurrentlyInscrit) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/connexion', { state: { from: '/evenements' } });
      return;
    }

    try {
      if (isCurrentlyInscrit) {
        await evenementService.desinscrire(evenementId);
        setSuccessMessage('Vous êtes désormais désinscrit de cet événement');
      } else {
        await evenementService.inscrire(evenementId, '');
        setSuccessMessage('Inscription réussie !');
      }

      setEvenements(prevEvents =>
        prevEvents.map(event =>
          event.id === evenementId
            ? { ...event, isInscrit: !isCurrentlyInscrit }
            : event
        )
      );

      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message || "Erreur lors de l'inscription");
      console.error(err);
    }
  };

  if (loading) return <div className="loading">Chargement en cours...</div>;
  if (error) return <div className="error">Erreur: {error}</div>;

  return (
    <div className="evenement-container">
      <Header />

      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Bouton filtre pour mobile */}
      <div className="filter-toggle">
        <button 
          className={`btn-filter ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FiFilter /> {showFilters ? 'Masquer les filtres' : 'Filtrer les événements'}
        </button>
      </div>

      {/* Filtres */}
      <div className={`filters-container ${showFilters ? 'show' : ''}`}>
        <div className="filters-header">
          <h3>Filtrer les événements</h3>
          <button className="close-filters" onClick={() => setShowFilters(false)}>
            <FiX />
          </button>
        </div>
        
        <div className="filter-group">
          <label>Thème</label>
          <select 
            value={filters.theme} 
            onChange={(e) => handleFilterChange('theme', e.target.value)}
          >
            <option value="">Tous les thèmes</option>
            {themes.map(theme => (
              <option key={theme} value={theme}>{theme}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Lieu</label>
          <select 
            value={filters.lieu} 
            onChange={(e) => {
              handleFilterChange('lieu', e.target.value);
              // Si webinaire est sélectionné, définir automatiquement "France entière"
              if (e.target.value === 'Webinaire') {
                handleFilterChange('ville', 'France entière');
              } else if (filters.ville === 'France entière') {
                handleFilterChange('ville', '');
              }
            }}
          >
            <option value="">Tous les lieux</option>
            {lieux.map(lieu => (
              <option key={lieu} value={lieu}>{lieu}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Ville/Région</label>
          <div className="autocomplete-container" ref={suggestionsRef}>
            <input
              type="text"
              value={filters.ville}
              onChange={(e) => handleCityInputChange(e.target.value)}
              onFocus={() => {
                setIsInputFocused(true);
                if (filters.ville.length > 1) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={() => setIsInputFocused(false)}
              disabled={filters.lieu === 'Webinaire'}
              placeholder="Rechercher une ville ou région"
              className="city-input"
            />
            <FiSearch className="search-icon" />
            {showSuggestions && suggestions.length > 0 && (
              <ul className="suggestions-list">
                {suggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="suggestion-item"
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {filters.lieu === 'Webinaire' && (
            <p className="filter-note">Les webinaires sont accessibles partout en France</p>
          )}
        </div>

        <button className="btn-reset" onClick={resetFilters}>
          Réinitialiser les filtres
        </button>
      </div>

      {/* Résultats de filtrage */}
      <div className="results-info">
        <p>{filteredEvenements.length} événement(s) trouvé(s)</p>
      </div>

      {/* Liste des événements */}
      <div className="evenement-list">
        {filteredEvenements.length > 0 ? (
          filteredEvenements.map((evenement) => (
            <div key={evenement.id} className="evenement-card">
              <div className="evenement-image-container">
                <img 
                  src={evenement.imageUrl || getImageUrl(evenement.image)} 
                  alt={evenement.titre}
                  className="evenement-image"
                  loading="lazy"
                  onError={(e) => {
                    console.error('Erreur de chargement de l\'image:', {
                      chemin: evenement.image,
                      url: evenement.imageUrl || getImageUrl(evenement.image),
                      eventId: evenement.id
                    });
                    e.target.onerror = null;
                    e.target.src = '/default-event.jpg';
                  }}
                />
              </div>

              <h3>{evenement.titre}</h3>
              <p className="description">{evenement.description}</p>

              <div className="evenement-details">
                <div className="detail-item">
                  <FiMapPin className="icon" />
                  <span>{evenement.lieu}</span>
                </div>

                <div className="detail-item">
                  <FiCalendar className="icon" />
                  <span>{new Date(evenement.date).toLocaleString('fr-FR')}</span>
                </div>

                {evenement.prix && (
                  <div className="detail-item">
                    <FiDollarSign className="icon" />
                    <span>{parseFloat(evenement.prix).toFixed(2)} €</span>
                  </div>
                )}

                {/* Ajout du lien Meet */}
                {evenement.meet_link && (
                  <div className="detail-item meet-link-container">
                    <FiVideo className="icon" />
                    <a 
                      href={evenement.meet_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="meet-link"
                    >
                      Rejoindre la réunion
                    </a>
                  </div>
                )}
              </div>

              <div className="evenement-meta">
                <FiUser className="icon" />
                <span>Organisé par: {evenement.user?.name || `User #${evenement.user_id}`}</span>
                <br />
                <span>Sous-service: {evenement.sous_service?.nom || `#${evenement.sous_service_id}`}</span>
              </div>

              <button 
                className={`btn-inscription ${evenement.isInscrit ? 'inscrit' : ''}`}
                onClick={() => handleInscription(evenement.id, evenement.isInscrit)}
              >
                {evenement.isInscrit ? 'Se désinscrire' : "S'inscrire"}
              </button>
            </div>
          ))
        ) : (
          <div className="no-results">
            <p>Aucun événement ne correspond à vos critères de recherche.</p>
            <button onClick={resetFilters}>Réinitialiser les filtres</button>
          </div>
        )}
      </div>

      {lastPage > 1 && filteredEvenements.length > 0 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Précédent
          </button>
          <span>Page {currentPage} sur {lastPage}</span>
          <button 
            onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
            disabled={currentPage === lastPage}
          >
            Suivant
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Evenement;